import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  NUTRIENT_KEYS,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
  REQUIRED_NUTRIENT_KEYS,
  canConvertUnit,
  formatNumber,
  fromBaseQuantity,
  getUnit,
  missingNutrientKeys,
  toBaseQuantity,
  type BaseUnit,
  type IngredientPackage,
  type NutrientKey,
  type Nutrients,
  type UnitsBySystem,
} from '@crave/costing';
import { ALLERGENS, ALLERGEN_LABELS } from '@crave/shared';
import {
  AllergenList,
  Button,
  Card,
  Field,
  FormAction,
  FormGrid,
  NutritionFactsLabel,
  SelectField,
  TextAreaField,
  allergenText,
} from '@crave/ui';
import { ApiError } from '@crave/api-client';
import { api } from '../api.ts';
import { BarcodeScanner } from '../components/BarcodeScanner.tsx';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { formatQty, useMeasureStyle } from '../components/MeasureIn.tsx';
import {
  UnitSelect,
  defaultUnitValue,
  splitUnitValue,
  unavailableUnitsHint,
  unitLabel,
  unitValue,
} from '../components/UnitSelect.tsx';
import { LOCATION_TIMEZONE, Notice, Section, errorText } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

/** An ingredient row from GET /admin/recipes/ingredients. */
interface Ingredient {
  id: string;
  code: string;
  name: string;
  base_unit: BaseUnit;
  category: string | null;
  density_g_per_ml: number | null;
  each_weight_g: number | null;
  allergens: string[] | null;
  /** When someone confirmed the allergen list; null until checked. */
  allergens_reviewed_at: string | null;
  nutrition_per_100g: Nutrients | null;
  nutrition_source: string | null;
  nutrition_updated_at: string | null;
  /** Barcode of the product bought, saved by a barcode scan (migration 0027). */
  upc?: string | null;
  is_prep: boolean;
  is_active: boolean;
  display_unit: string | null;
  cost_cents_per_base: number | null;
  cost_source: string | null;
  packages: IngredientPackage[];
  convertible_units: UnitsBySystem;
  can_delete: boolean;
  delete_blocked_reason: string | null;
  uses: Record<string, number>;
}

interface IngredientsResponse {
  items: Ingredient[];
  units: { code: string; kind: string; name: string }[];
}

type Flash = { kind: 'ok' | 'error'; text: string } | null;

/** Same key and shape as the Recipes page, so both share one cached list. */
const INGREDIENTS_KEY = ['ingredients'];

const CSS = `
.ing-page { display: grid; gap: 16px; min-width: 0; }
.ing-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.ing-soft { color: var(--ink-soft); }
.ing-small { font-size: 13px; line-height: 1.35; }
.ing-toolbar { display: grid; gap: 12px; min-width: 0; }
.ing-search { max-width: 460px; }
.ing-chips { display: flex; flex-wrap: wrap; gap: 6px; border: 0; margin: 0; padding: 0; min-width: 0; }
.ing-chip { position: relative; display: inline-flex; }
.ing-chip input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; }
.ing-chip > span { display: inline-flex; align-items: center; gap: 6px; min-height: var(--touch); padding: 0 14px; border-radius: 999px;
  border: 2px solid var(--line); background: var(--paper); color: var(--ink); font-size: 14px; font-weight: 600; white-space: nowrap; }
.ing-chip input:checked + span { background: var(--ink); border-color: var(--ink); color: #fff; }
.ing-chip input:focus-visible + span { outline: 3px solid var(--crave-red); outline-offset: 2px; }
.ing-chip-count { font-weight: 400; opacity: 0.8; }
.ing-list { container-type: inline-size; min-width: 0; overflow-x: auto; }
.ing-link { font-weight: 600; color: var(--ink); text-underline-offset: 3px; }
.ing-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.ing-table th { text-align: left; padding: 8px 10px; border-bottom: 2px solid var(--line); color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.ing-table td { padding: 10px; border-bottom: 1px solid var(--line); vertical-align: top; }
.ing-table td.ing-empty { padding: 16px 10px; color: var(--ink-soft); }
.ing-table td.ing-cost { white-space: nowrap; }
.ing-name-link { font-weight: 700; color: var(--ink); text-underline-offset: 3px; }
.ing-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.ing-cell-label { display: none; }
@container (max-width: 980px) {
  .ing-table th, .ing-table td { padding-left: 5px; padding-right: 5px; }
}
@container (max-width: 720px) {
  .ing-table thead { display: none; }
  .ing-table, .ing-table tbody { display: block; }
  .ing-table tr { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 14px; padding: 14px 0; border-bottom: 1px solid var(--line); }
  .ing-table td { display: block; padding: 0; border: 0; min-width: 0; overflow-wrap: anywhere; }
  .ing-table td.ing-name, .ing-table td.ing-empty { grid-column: 1 / -1; }
  .ing-table td.ing-cost { white-space: normal; }
  .ing-cell-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
}
.ing-tag { display: inline-flex; align-items: center; padding: 1px 8px; border-radius: 999px; border: 1px solid currentColor;
  font-size: 12px; font-weight: 700; line-height: 1.5; white-space: nowrap; }
.ing-tag[data-tone='ok'] { color: var(--ok); }
.ing-tag[data-tone='warn'] { color: var(--warn); }
.ing-tag[data-tone='soft'] { color: var(--ink-soft); }
.ing-tag[data-tone='red'] { color: var(--crave-red); }
.ing-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; }
.ing-head h2 { margin: 0; overflow-wrap: anywhere; }
.ing-back { display: inline-flex; align-items: center; min-height: var(--touch); font-weight: 600; color: var(--ink-soft); text-underline-offset: 3px; }
.ing-card { display: grid; gap: 14px; min-width: 0; }
.ing-card h3 { margin: 0; font-size: 19px; }
.ing-card h4 { margin: 0; font-size: 16px; }
.ing-card p { margin: 0; }
.ing-block { display: grid; gap: 10px; padding-top: 16px; border-top: 1px solid var(--line); min-width: 0; }
.ing-intro { color: var(--ink-soft); font-size: 14px; line-height: 1.45; }
.ing-check-row { display: flex; align-items: center; gap: 10px; min-height: var(--touch); font-weight: 400; }
.ing-check-row input, .ing-allergen input { width: 20px; height: 20px; margin: 0; accent-color: var(--crave-red); flex: none; }
.ing-allergens { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 180px), 1fr)); gap: 2px 16px; border: 0; margin: 0; padding: 0; min-width: 0; }
.ing-allergen { display: flex; align-items: center; gap: 10px; min-height: var(--touch); font-size: 15px; cursor: pointer; }
.ing-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 10px; }
.ing-actions-note { margin-right: auto; font-size: 14px; color: var(--ink-soft); }
.ing-pkgs { list-style: none; margin: 0; padding: 0; display: grid; }
.ing-pkg { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 6px 12px; padding: 10px 0; border-bottom: 1px solid var(--line); }
.ing-pkg > div { min-width: 0; overflow-wrap: anywhere; }
.ing-bases { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr)); gap: 10px; border: 0; margin: 0; padding: 0; min-width: 0; }
.ing-bases legend { font-weight: 600; margin-bottom: 6px; padding: 0; }
.ing-base { display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; align-items: start; padding: 12px; border: 2px solid var(--line);
  border-radius: var(--radius-sm); background: var(--paper); cursor: pointer; }
.ing-base:has(input:checked) { border-color: var(--crave-red); }
.ing-base input { width: 20px; height: 20px; margin: 1px 0 0; accent-color: var(--crave-red); }
.ing-base strong { font-size: 15px; }
.ing-base span { grid-column: 2; font-size: 14px; color: var(--ink-soft); line-height: 1.4; }
.ing-nutri { container-type: inline-size; min-width: 0; }
.ing-nutri-grid { display: grid; gap: 20px; min-width: 0; }
.ing-nutri-fields { display: grid; gap: 14px; min-width: 0; }
.ing-preview { display: grid; gap: 8px; justify-items: start; min-width: 0; }
.ing-preview-empty { width: 100%; max-width: 330px; padding: 16px; border: 2px dashed var(--line); border-radius: var(--radius-sm); color: var(--ink-soft); font-size: 14px; }
@container (min-width: 700px) {
  .ing-nutri-grid { grid-template-columns: minmax(0, 1fr) 300px; align-items: start; }
  .ing-preview { position: sticky; top: 16px; }
}
.ing-saved { font-size: 14px; }
.ing-saved summary { cursor: pointer; font-weight: 600; min-height: var(--touch); display: flex; align-items: center; }
.ing-saved dl { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 12px; margin: 6px 0 0; }
.ing-saved dt { color: var(--ink-soft); }
.ing-saved dd { margin: 0; text-align: right; font-variant-numeric: tabular-nums; }
.ing-danger { border-color: var(--crave-red) !important; }
.ing-barcode { display: grid; gap: 10px; padding: 14px; border: 2px solid var(--line); border-radius: var(--radius-sm); min-width: 0; }
.ing-saved-upc { display: grid; gap: 8px; min-width: 0; overflow-wrap: anywhere; }
.ing-saved-upc p { margin: 0; }
.ing-review { display: grid; gap: 12px; padding-top: 12px; border-top: 1px solid var(--line); min-width: 0; }
.ing-review-facts { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 4px 12px; margin: 0; font-size: 15px; }
.ing-review-facts dt { color: var(--ink-soft); }
.ing-review-facts dd { margin: 0; font-weight: 600; overflow-wrap: anywhere; }
.ing-compare-wrap { min-width: 0; overflow-x: auto; }
.ing-compare { width: 100%; border-collapse: collapse; font-size: 14px; font-variant-numeric: tabular-nums; }
.ing-compare th, .ing-compare td { padding: 8px 6px; border-bottom: 1px solid var(--line); text-align: right; }
.ing-compare thead th { color: var(--ink-soft); font-weight: 600; vertical-align: bottom; }
.ing-compare th[scope='row'], .ing-compare thead th:first-child { text-align: left; font-weight: 600; }
.ing-compare tr[data-changed] td:last-child { font-weight: 700; color: var(--crave-red); }
.ing-suggest { display: grid; gap: 4px; padding: 10px 12px; border: 2px dashed var(--warn); border-radius: var(--radius-sm); font-size: 15px; line-height: 1.4; }
`;

const FILTERS = [
  ['all', 'All'],
  ['nutrition', 'Missing nutrition'],
  ['conversions', 'Missing conversions'],
  ['allergens', 'Has allergens'],
  ['unchecked', 'Allergens not checked'],
  ['inactive', 'Inactive'],
] as const;
type FilterKey = (typeof FILTERS)[number][0];
const isFilter = (v: string | null): v is FilterKey => FILTERS.some(([k]) => k === v);

const BASE_WORD: Record<BaseUnit, string> = { g: 'Grams', ml: 'Milliliters', each: 'Each' };

const BASE_CHOICES: readonly (readonly [BaseUnit, string, string])[] = [
  [
    'g',
    'By weight',
    'Flour, cheese, meats, vegetables. Pounds, ounces and cups still work once it is set up.',
  ],
  [
    'ml',
    'By volume',
    'Oil, vinegar, water and other liquids. Fluid ounces, cups and quarts still work.',
  ],
  ['each', 'By count', 'Things you count one at a time, like dough balls, eggs or boxes.'],
];

const VOLUMES = [
  ['cup', '1 cup'],
  ['tbsp', '1 tablespoon'],
  ['floz', '1 fluid ounce'],
] as const;
type Volume = (typeof VOLUMES)[number][0];
const WEIGHTS = [
  ['g', 'Grams'],
  ['oz', 'Ounces'],
] as const;
type Weight = (typeof WEIGHTS)[number][0];

const OPTIONAL_NUTRIENTS = new Set<NutrientKey>(
  NUTRIENT_KEYS.filter((k) => !(REQUIRED_NUTRIENT_KEYS as readonly string[]).includes(k)),
);

/* ---------- small helpers ---------- */

const factor = (code: string) => getUnit(code)?.toBase ?? 1;

/** A number as a plain string for an input: enough decimals for small amounts, none trailing. */
function plain(n: number): string {
  const abs = Math.abs(n);
  const decimals = abs < 1 ? 3 : abs < 10 ? 2 : 1;
  return String(Number(n.toFixed(decimals)));
}

/** "1.5", "1,000", "1/4" or "1 1/2". Null when empty, NaN when unreadable. */
function parseAmount(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (!t) return null;
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const frac = /^(\d+)\/(\d+)$/.exec(t);
  if (frac) return Number(frac[1]) / Number(frac[2]);
  if (!/^(\d+\.?\d*|\.\d+)$/.test(t)) return NaN;
  return Number(t);
}

const isNumber = (n: number | null): n is number => n !== null && Number.isFinite(n);

/**
 * Reference values (USDA) rather than the label of the product actually bought. A barcode scan
 * ("usda_branded:…") is the real product's label, so it is not a reference.
 */
const isReference = (source: string | null | undefined) =>
  Boolean(source && !BARCODE_SOURCE.test(source) && /usda|reference value/i.test(source));

function hasNutrition(i: Ingredient): boolean {
  return Boolean(i.nutrition_per_100g && Object.keys(i.nutrition_per_100g).length > 0);
}

/** Needs nutrition typed in: not made from a recipe, and some required value is missing. */
function missingNutrition(i: Ingredient): boolean {
  return !i.is_prep && missingNutrientKeys(i.nutrition_per_100g).length > 0;
}

function nutritionTag(i: Ingredient): { tone: string; text: string } {
  if (i.is_prep) return { tone: 'soft', text: 'From its recipe' };
  if (!hasNutrition(i)) return { tone: 'warn', text: 'Missing' };
  const missing = missingNutrientKeys(i.nutrition_per_100g).length;
  if (missing) return { tone: 'warn', text: `${missing} values missing` };
  if (isReference(i.nutrition_source)) return { tone: 'soft', text: 'Reference values' };
  return { tone: 'ok', text: 'Filled in' };
}

/** What conversion is missing, in a few words, or null. */
function conversionGap(i: Ingredient): string | null {
  if (!unavailableUnitsHint(i)) return null;
  return i.base_unit === 'each' ? 'No weight for one' : 'No cup weight';
}

function dollars(cents: number): string {
  const small = cents > 0 && cents < 10;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: small ? 3 : 2,
  }).format(cents / 100);
}

/** "$1.12 / lb": per its display unit, else per pound, fluid ounce or each. */
function costText(i: Ingredient): string | null {
  if (i.cost_cents_per_base === null || i.cost_cents_per_base === undefined) return null;
  const fallback = i.base_unit === 'g' ? 'lb' : i.base_unit === 'ml' ? 'floz' : 'each';
  const unit = i.display_unit && canConvertUnit(i.display_unit, i) ? i.display_unit : fallback;
  try {
    const perUnit = Number(i.cost_cents_per_base) * toBaseQuantity(1, unit, i);
    return `${dollars(perUnit)} / ${getUnit(unit)?.abbr ?? unit}`;
  } catch {
    return `${dollars(Number(i.cost_cents_per_base))} / ${i.base_unit}`;
  }
}

function makeCode(name: string, taken: ReadonlySet<string>): string {
  let base = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 34)
    .replace(/_+$/g, '');
  if (base.length < 2) base = 'ingredient';
  let code = base;
  for (let n = 2; taken.has(code); n++) code = `${base}_${n}`;
  return code;
}

function Tag({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className="ing-tag" data-tone={tone}>
      {children}
    </span>
  );
}

/** A radio group drawn as pills (filters, nutrition entry mode). */
function Chips<T extends string>({
  legend,
  value,
  onChange,
  options,
}: {
  legend: string;
  value: T;
  onChange: (v: T) => void;
  options: readonly (readonly [T, ReactNode])[];
}) {
  const name = useId();
  return (
    <fieldset className="ing-chips">
      <legend className="ing-sr">{legend}</legend>
      {options.map(([v, l]) => (
        <label key={v} className="ing-chip">
          <input
            type="radio"
            name={name}
            value={v}
            checked={value === v}
            onChange={() => onChange(v)}
          />
          <span>{l}</span>
        </label>
      ))}
    </fieldset>
  );
}

/* ---------- page ---------- */

export function Ingredients() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');
  const q = useQuery({
    queryKey: INGREDIENTS_KEY,
    queryFn: async () => (await api.get<IngredientsResponse>('/admin/recipes/ingredients')).data,
  });
  const [flash, setFlash] = useState<Flash>(null);
  const items = q.data?.items ?? [];
  const categories = [...new Set(items.map((i) => i.category).filter((c): c is string => !!c))];

  const listParams = new URLSearchParams(params);
  listParams.delete('id');
  const listHref = `/ingredients${listParams.size ? `?${listParams.toString()}` : ''}`;

  const open = (id: string | null, message: Flash = null) => {
    setFlash(message);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('id', id);
      else next.delete('id');
      return next;
    });
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [selectedId]);

  const body = (() => {
    if (q.isLoading) return <p className="ing-soft">Loading ingredients…</p>;
    if (q.isError)
      return (
        <Notice kind="error">
          Ingredients did not load. {errorText(q.error, 'Check the connection and reload.')}
        </Notice>
      );
    if (selectedId) {
      const ing = items.find((i) => i.id === selectedId);
      if (!ing)
        return (
          <>
            <Link to={listHref} className="ing-back">
              Back to all ingredients
            </Link>
            <Notice>That ingredient is not here anymore. It may have been deleted.</Notice>
          </>
        );
      return (
        <IngredientEditor
          key={ing.id}
          ing={ing}
          categories={categories}
          listHref={listHref}
          flash={flash}
          setFlash={setFlash}
          onGone={(text) => open(null, { kind: 'ok', text })}
        />
      );
    }
    return (
      <IngredientList
        items={items}
        categories={categories}
        flash={flash}
        setFlash={setFlash}
        onCreated={(id, name) =>
          open(id, {
            kind: 'ok',
            text: `Added ${name}. Now set how it converts, its allergens and its nutrition.`,
          })
        }
      />
    );
  })();

  return (
    <div className="ing-page">
      <style href="crave-ingredients-page" precedence="default">
        {CSS}
      </style>
      {body}
    </div>
  );
}

/* ---------- list ---------- */

function IngredientList({
  items,
  categories,
  flash,
  setFlash,
  onCreated,
}: {
  items: Ingredient[];
  categories: string[];
  flash: Flash;
  setFlash: (f: Flash) => void;
  onCreated: (id: string, name: string) => void;
}) {
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const shown = params.get('show');
  const filter: FilterKey = isFilter(shown) ? shown : 'all';
  const [adding, setAdding] = useState(false);

  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value && !(key === 'show' && value === 'all')) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const tests: Record<FilterKey, (i: Ingredient) => boolean> = {
    all: () => true,
    nutrition: missingNutrition,
    conversions: (i) => conversionGap(i) !== null,
    allergens: (i) => (i.allergens?.length ?? 0) > 0,
    unchecked: (i) => i.allergens_reviewed_at === null,
    inactive: (i) => !i.is_active,
  };
  const needle = search.trim().toLowerCase();
  const matches = (i: Ingredient) =>
    !needle ||
    [i.name, i.category ?? '', allergenText(i.allergens)].some((s) =>
      s.toLowerCase().includes(needle),
    );
  const rows = items.filter((i) => tests[filter](i) && matches(i));

  const head = [
    'Name',
    'Category',
    'Measured in',
    'Show and count in',
    'Allergens',
    'Nutrition',
    'Cost',
  ];

  return (
    <Section
      title="Ingredients"
      right={
        !adding && (
          <Button
            onClick={() => {
              setFlash(null);
              setAdding(true);
            }}
          >
            Add ingredient
          </Button>
        )
      }
    >
      {flash && <Notice kind={flash.kind}>{flash.text}</Notice>}
      {adding && (
        <AddIngredient
          items={items}
          categories={categories}
          onCancel={() => setAdding(false)}
          onCreated={onCreated}
        />
      )}
      <div className="ing-toolbar">
        <Field
          className="ing-search"
          label="Search"
          type="search"
          placeholder="Name, category or allergen"
          value={search}
          onChange={(e) => setParam('q', e.target.value)}
          autoComplete="off"
        />
        <Chips
          legend="Show"
          value={filter}
          onChange={(v) => setParam('show', v)}
          options={FILTERS.map(([k, l]) => [
            k,
            <>
              {l} <span className="ing-chip-count">{items.filter(tests[k]).length}</span>
            </>,
          ])}
        />
      </div>
      <div className="ing-list">
        <table className="ing-table">
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="ing-empty" colSpan={head.length}>
                  {items.length === 0
                    ? 'No ingredients yet. Add the first one above.'
                    : 'No ingredients match. Clear the search or pick All.'}
                </td>
              </tr>
            )}
            {rows.map((i) => {
              const nut = nutritionTag(i);
              const gap = conversionGap(i);
              const cost = costText(i);
              return (
                <tr key={i.id}>
                  <td className="ing-name">
                    <Link
                      className="ing-name-link crave-hit"
                      to={`/ingredients?${new URLSearchParams([
                        ...params.entries(),
                        ['id', i.id],
                      ]).toString()}`}
                      onClick={() => setFlash(null)}
                    >
                      {i.name}
                    </Link>
                    {(!i.is_active || i.is_prep) && (
                      <div className="ing-tags">
                        {!i.is_active && <Tag tone="red">Inactive</Tag>}
                        {i.is_prep && <Tag tone="soft">Made in the kitchen</Tag>}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="ing-cell-label">Category</span>
                    {i.category ?? <span className="ing-soft">None</span>}
                  </td>
                  <td>
                    <span className="ing-cell-label">Measured in</span>
                    {BASE_WORD[i.base_unit]}
                    {gap && <div className="ing-small ing-soft">{gap}</div>}
                  </td>
                  <td>
                    <span className="ing-cell-label">Show and count in</span>
                    {i.display_unit ? (
                      unitLabel(i.display_unit)
                    ) : (
                      <span className="ing-soft">Not set</span>
                    )}
                  </td>
                  <td>
                    <span className="ing-cell-label">Allergens</span>
                    {allergenText(i.allergens) || <span className="ing-soft">None</span>}
                    {i.allergens_reviewed_at === null && (
                      <div className="ing-tags">
                        <Tag tone="warn">Not checked</Tag>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="ing-cell-label">Nutrition</span>
                    <Tag tone={nut.tone}>{nut.text}</Tag>
                  </td>
                  <td className="ing-cost">
                    <span className="ing-cell-label">Cost</span>
                    {cost ?? <span className="ing-soft">No cost yet</span>}
                    {cost && i.cost_source && (
                      <div className="ing-small ing-soft">
                        {i.cost_source === 'manual' ? 'Set by hand' : 'From purchases'}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function AddIngredient({
  items,
  categories,
  onCancel,
  onCreated,
}: {
  items: Ingredient[];
  categories: string[];
  onCancel: () => void;
  onCreated: (id: string, name: string) => void;
}) {
  const qc = useQueryClient();
  const listId = useId();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [base, setBase] = useState<BaseUnit>('g');
  const [error, setError] = useState<string | null>(null);
  const taken = new Set(items.map((i) => i.code));
  const duplicate = items.find((i) => i.name.trim().toLowerCase() === name.trim().toLowerCase());

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      const body: Record<string, unknown> = {
        code: makeCode(trimmed, taken),
        name: trimmed,
        base_unit: base,
      };
      if (category.trim()) body['category'] = category.trim();
      return (await api.post<{ id: string }>('/admin/recipes/ingredients', body)).data;
    },
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      onCreated(data.id, name.trim());
    },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <Card>
      <form
        className="ing-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) {
            setError('Give the ingredient a name.');
            return;
          }
          setError(null);
          create.mutate();
        }}
      >
        <h3>Add ingredient</h3>
        <FormGrid min={240}>
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Fresh basil"
            maxLength={120}
            required
            hint={duplicate ? `${duplicate.name} is already on the list.` : undefined}
          />
          <Field
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Produce"
            list={listId}
            maxLength={60}
            hint="Optional. Pick one you already use or type a new one."
          />
        </FormGrid>
        <datalist id={listId}>
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <fieldset className="ing-bases">
          <legend>How do you measure it?</legend>
          {BASE_CHOICES.map(([code, title, text]) => (
            <label key={code} className="ing-base">
              <input
                type="radio"
                name="ing-base"
                value={code}
                checked={base === code}
                onChange={() => setBase(code)}
              />
              <strong>{title}</strong>
              <span>{text}</span>
            </label>
          ))}
        </fieldset>
        <p className="ing-intro">
          This is how Crave keeps the amounts behind the scenes and it can't be changed later. You
          can still enter and see it in any unit you like.
        </p>
        {error && <Notice kind="error">{error}</Notice>}
        <div className="ing-actions">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add ingredient'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ---------- editor ---------- */

function IngredientEditor({
  ing,
  categories,
  listHref,
  flash,
  setFlash,
  onGone,
}: {
  ing: Ingredient;
  categories: string[];
  listHref: string;
  flash: Flash;
  setFlash: (f: Flash) => void;
  onGone: (text: string) => void;
}) {
  const { can } = useStaff();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
  const [confirming, setConfirming] = useState(false);
  const [refused, setRefused] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const deactivate = async () => {
    await api.patch(`/admin/recipes/ingredients/${ing.id}`, { is_active: false });
    await refresh();
    setFlash({
      kind: 'ok',
      text: `${ing.name} is inactive. It left the pickers and its history stays. Tick Active to bring it back.`,
    });
  };

  const detailsKey = [
    ing.name,
    ing.category,
    ing.is_active,
    ing.density_g_per_ml,
    ing.each_weight_g,
    ing.display_unit,
    (ing.allergens ?? []).join(','),
    ing.allergens_reviewed_at,
  ].join('|');
  const nutritionKey = [
    ing.nutrition_updated_at,
    ing.nutrition_source,
    JSON.stringify(ing.nutrition_per_100g),
  ].join('|');
  const packageCount = ing.packages.length;

  return (
    <div className="ing-page">
      <Link to={listHref} className="ing-back" onClick={() => setFlash(null)}>
        Back to all ingredients
      </Link>
      <div className="ing-head">
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <h2>{ing.name}</h2>
          <div className="ing-tags">
            <Tag tone={ing.is_active ? 'ok' : 'red'}>{ing.is_active ? 'Active' : 'Inactive'}</Tag>
            {ing.is_prep && <Tag tone="soft">Made in the kitchen</Tag>}
            <Tag tone="soft">Kept in {BASE_WORD[ing.base_unit].toLowerCase()}</Tag>
          </div>
        </div>
        <HistoryLink entityType="ingredients" entityId={ing.id} name={ing.name} />
      </div>
      {flash && <Notice kind={flash.kind}>{flash.text}</Notice>}

      <DetailsCard key={detailsKey} ing={ing} categories={categories} setFlash={setFlash} />
      <PackagesCard ing={ing} setFlash={setFlash} />
      {ing.is_prep ? (
        <Card>
          <div className="ing-card">
            <h3>Nutrition</h3>
            <p className="ing-intro">
              {ing.name} is made in the kitchen from a recipe, so its nutrition comes from the
              ingredients in that recipe. Fill those in and it adds up by itself.
            </p>
            <p>
              <Link to="/recipes" className="ing-link">
                Open recipes
              </Link>
            </p>
          </div>
        </Card>
      ) : (
        <NutritionCard key={nutritionKey} ing={ing} setFlash={setFlash} />
      )}

      {can('records.delete') && (
        <Card className="ing-danger">
          <div className="ing-card">
            <h3>Delete ingredient</h3>
            {ing.can_delete ? (
              <>
                <p className="ing-intro">
                  Nothing has used {ing.name} yet, so it can be deleted for good.
                </p>
                <div className="ing-actions" style={{ justifyContent: 'flex-start' }}>
                  <Button variant="danger" onClick={() => setConfirming(true)}>
                    Delete {ing.name}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="ing-intro">
                  {ing.delete_blocked_reason ??
                    `${ing.name} has history, so it can't be deleted. Deactivate it instead.`}
                </p>
                {ing.is_active ? (
                  <div className="ing-actions" style={{ justifyContent: 'flex-start' }}>
                    <Button
                      variant="secondary"
                      disabled={deactivating}
                      onClick={() => {
                        setDeactivating(true);
                        deactivate()
                          .catch((e: unknown) => setFlash({ kind: 'error', text: errorText(e) }))
                          .finally(() => setDeactivating(false));
                      }}
                    >
                      {deactivating ? 'Deactivating…' : 'Deactivate'}
                    </Button>
                  </div>
                ) : (
                  <p className="ing-intro">It is already inactive, so it stays out of pickers.</p>
                )}
              </>
            )}
          </div>
        </Card>
      )}

      <ConfirmDelete
        open={confirming}
        onClose={() => {
          setConfirming(false);
          setRefused(false);
        }}
        title={`Delete ${ing.name}?`}
        description={
          refused
            ? undefined
            : 'Nothing in recipes, inventory or purchasing has used it, so it will be gone for good. A record of it stays in the audit trail.'
        }
        consequences={[
          ...(packageCount
            ? [`Its ${packageCount === 1 ? 'package' : `${packageCount} packages`}`]
            : []),
          'Any stock record and planned purchases for it',
          ...(ing.is_prep ? ['The recipe that makes it'] : []),
        ]}
        askReason
        onConfirm={async ({ reason }) => {
          const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
          try {
            await api.delete(`/admin/recipes/ingredients/${ing.id}${query}`);
          } catch (e) {
            // Something used it since the page loaded: drop the "nothing used it" sentence.
            if (e instanceof ApiError && e.code === 'DELETE_BLOCKED') setRefused(true);
            throw e;
          }
          onGone(`Deleted ${ing.name}.`);
          void refresh();
        }}
        alternative={{
          label: 'Deactivate',
          pendingLabel: 'Deactivating…',
          description: 'Deactivating hides it from pickers and keeps its history.',
          onClick: deactivate,
        }}
      />
    </div>
  );
}

/* ---------- details: name, conversions, display unit, allergens ---------- */

interface WeightDraft {
  text: string;
  /** Grams per ml (cup weight) or grams (each weight); null when empty, NaN when unreadable. */
  value: number | null;
}

function DetailsCard({
  ing,
  categories,
  setFlash,
}: {
  ing: Ingredient;
  categories: string[];
  setFlash: (f: Flash) => void;
}) {
  const qc = useQueryClient();
  const listId = useId();
  const [style] = useMeasureStyle();
  const usWeights = style === 'us_weight' || style === 'us_volume' || style === 'imperial';
  const [name, setName] = useState(ing.name);
  const [category, setCategory] = useState(ing.category ?? '');
  const [active, setActive] = useState(ing.is_active);
  const [allergens, setAllergens] = useState<string[]>(ing.allergens ?? []);
  const wasReviewed = ing.allergens_reviewed_at !== null;
  const [reviewed, setReviewed] = useState(wasReviewed);
  const reviewHintId = useId();
  const [display, setDisplay] = useState(unitValue(ing.display_unit));

  // Cup weight: "1 cup weighs 120 g". The stored value is grams per ml.
  const [volume, setVolume] = useState<Volume>('cup');
  const [cupWeight, setCupWeight] = useState<Weight>(usWeights ? 'oz' : 'g');
  const densityText = (d: number | null, vol: Volume, wt: Weight) =>
    d === null ? '' : plain((d * factor(vol)) / factor(wt));
  const [density, setDensity] = useState<WeightDraft>({
    text: densityText(ing.density_g_per_ml, volume, cupWeight),
    value: ing.density_g_per_ml,
  });
  const [eachWeight, setEachWeight] = useState<Weight>(usWeights ? 'oz' : 'g');
  const eachText = (g: number | null, wt: Weight) => (g === null ? '' : plain(g / factor(wt)));
  const [each, setEach] = useState<WeightDraft>({
    text: eachText(ing.each_weight_g, eachWeight),
    value: ing.each_weight_g,
  });

  const typeDensity = (text: string) => {
    const n = parseAmount(text);
    const value =
      n === null ? null : isNumber(n) && n > 0 ? (n * factor(cupWeight)) / factor(volume) : NaN;
    // Typing back what was saved keeps the saved value exactly.
    const same = text === densityText(ing.density_g_per_ml, volume, cupWeight);
    setDensity({ text, value: same ? ing.density_g_per_ml : value });
  };
  const typeEach = (text: string) => {
    const n = parseAmount(text);
    const value = n === null ? null : isNumber(n) && n > 0 ? n * factor(eachWeight) : NaN;
    const same = text === eachText(ing.each_weight_g, eachWeight);
    setEach({ text, value: same ? ing.each_weight_g : value });
  };
  const densityError =
    density.value !== null && !Number.isFinite(density.value)
      ? 'Enter a weight above zero, like 120.'
      : density.value !== null && density.value > 100
        ? 'That is too heavy for the measure. Check the number and the unit.'
        : undefined;
  const eachError =
    each.value !== null && !Number.isFinite(each.value)
      ? 'Enter a weight above zero, like 3.'
      : each.value !== null && each.value > 100_000
        ? 'That is over 100 kg. Check the number and the unit.'
        : undefined;

  const draft = {
    ...ing,
    name: name.trim() || ing.name,
    density_g_per_ml: densityError ? ing.density_g_per_ml : density.value,
    each_weight_g: eachError ? ing.each_weight_g : each.value,
  };
  const fields = splitUnitValue(display);
  const displayUnit = 'unit' in fields ? fields.unit : null;
  const displayError =
    displayUnit && displayUnit !== ing.display_unit && !canConvertUnit(displayUnit, draft)
      ? `${unitLabel(displayUnit)} needs the conversion above.`
      : undefined;
  const nameError = name.trim() ? undefined : 'Give the ingredient a name.';

  const body: Record<string, unknown> = {};
  if (name.trim() !== ing.name) body['name'] = name.trim();
  if ((category.trim() || null) !== (ing.category ?? null))
    body['category'] = category.trim() || null;
  if (active !== ing.is_active) body['is_active'] = active;
  if (density.value !== ing.density_g_per_ml) body['density_g_per_ml'] = density.value;
  if (each.value !== ing.each_weight_g) body['each_weight_g'] = each.value;
  if (displayUnit !== ing.display_unit) body['display_unit'] = displayUnit;
  const sorted = (a: readonly string[]) => [...a].sort().join(',');
  const allergensChanged = sorted(allergens) !== sorted(ing.allergens ?? []);
  if (allergensChanged) body['allergens'] = allergens;
  // A changed list that stays ticked is confirmed again, so it gets today's date.
  if (reviewed !== wasReviewed || (reviewed && allergensChanged))
    body['allergens_reviewed'] = reviewed;
  const dirty = Object.keys(body).length > 0;
  const checkedOn = ing.allergens_reviewed_at
    ? new Date(ing.allergens_reviewed_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: LOCATION_TIMEZONE,
      })
    : null;
  const reviewHint = reviewed
    ? checkedOn && !allergensChanged
      ? `Checked on ${checkedOn}.`
      : 'Saving records today as the day you checked.'
    : wasReviewed
      ? 'Saving removes the check, so pizzas with this ingredient will tell guests to ask you about allergens.'
      : 'Not checked yet, so pizzas with this ingredient tell guests to ask you about allergens.';
  const invalid = Boolean(nameError || densityError || eachError || displayError);

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.patch<{ ok: true; display_unit_cleared?: boolean }>(
          `/admin/recipes/ingredients/${ing.id}`,
          body,
        )
      ).data,
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      setFlash({
        kind: 'ok',
        text: data.display_unit_cleared
          ? `Saved. "Show and count in" went back to ${BASE_WORD[ing.base_unit].toLowerCase()}, because the unit it used needs the conversion you removed.`
          : `Saved ${name.trim()}.`,
      });
    },
    onError: (e) => setFlash({ kind: 'error', text: errorText(e) }),
  });

  const reset = () => {
    setName(ing.name);
    setCategory(ing.category ?? '');
    setActive(ing.is_active);
    setAllergens(ing.allergens ?? []);
    setReviewed(wasReviewed);
    setDisplay(unitValue(ing.display_unit));
    setDensity({
      text: densityText(ing.density_g_per_ml, volume, cupWeight),
      value: ing.density_g_per_ml,
    });
    setEach({ text: eachText(ing.each_weight_g, eachWeight), value: ing.each_weight_g });
  };

  const volumeName = VOLUMES.find(([v]) => v === volume)?.[1] ?? '1 cup';
  const showEach = ing.base_unit !== 'ml' || ing.each_weight_g !== null;
  const cupHint =
    ing.base_unit === 'ml'
      ? 'Lets you use grams, ounces and pounds for it.'
      : 'Lets you use cups, tablespoons and teaspoons for it.';
  const eachHint =
    ing.base_unit === 'each'
      ? 'Lets you use grams, ounces and pounds for it.'
      : 'Lets you count it one at a time, like a clove or a leaf.';

  return (
    <Card>
      <form
        className="ing-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !invalid) {
            setFlash(null);
            save.mutate();
          }
        }}
      >
        <h3>Details</h3>
        <FormGrid min={220}>
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            error={nameError}
          />
          <Field
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            list={listId}
            maxLength={60}
            placeholder="None"
          />
          <label className="crave-field">
            <span className="crave-field-label">Status</span>
            <span className="ing-check-row">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              Active
            </span>
            <span className="crave-field-hint">
              {active
                ? 'Shows in recipes, inventory and purchasing.'
                : 'Hidden from pickers. Its history stays.'}
            </span>
          </label>
        </FormGrid>
        <datalist id={listId}>
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        <div className="ing-block">
          <h4>Conversions</h4>
          <p className="ing-intro">
            Weigh it once the way you would in the kitchen. Crave uses these to turn cups, spoons,
            pounds and counts into the same amount.
          </p>
          {ing.base_unit !== 'each' && (
            <FormGrid min={170}>
              <SelectField
                label="Measure"
                value={volume}
                options={VOLUMES}
                onChange={(e) => {
                  const next = e.target.value as Volume;
                  setVolume(next);
                  if (isNumber(density.value))
                    setDensity({ ...density, text: densityText(density.value, next, cupWeight) });
                }}
              />
              <Field
                label={`${volumeName} weighs`}
                inputMode="decimal"
                value={density.text}
                onChange={(e) => typeDensity(e.target.value)}
                placeholder="Not set"
                error={densityError}
                hint={cupHint}
              />
              <SelectField
                label="In"
                value={cupWeight}
                options={WEIGHTS}
                onChange={(e) => {
                  const next = e.target.value as Weight;
                  setCupWeight(next);
                  if (isNumber(density.value))
                    setDensity({ ...density, text: densityText(density.value, volume, next) });
                }}
              />
            </FormGrid>
          )}
          {showEach && (
            <FormGrid min={170}>
              <Field
                label="1 each weighs"
                inputMode="decimal"
                value={each.text}
                onChange={(e) => typeEach(e.target.value)}
                placeholder="Not set"
                error={eachError}
                hint={eachHint}
              />
              <SelectField
                label="In"
                value={eachWeight}
                options={WEIGHTS}
                onChange={(e) => {
                  const next = e.target.value as Weight;
                  setEachWeight(next);
                  if (isNumber(each.value)) setEach({ ...each, text: eachText(each.value, next) });
                }}
              />
            </FormGrid>
          )}
          <p className="ing-intro">Empty a box to remove that conversion.</p>
        </div>

        <div className="ing-block">
          <h4>Show and count in</h4>
          <FormGrid min={260}>
            <UnitSelect
              label="Unit"
              ingredient={draft}
              value={display}
              onValueChange={(v) => setDisplay(v)}
              includePackages={false}
              placeholder={`${BASE_WORD[ing.base_unit]} (standard)`}
              showUnavailableHint={false}
              hint={`Inventory counts, stock and costs show it in this unit.${
                conversionGap(draft)
                  ? ing.base_unit === 'g'
                    ? ' Set the cup weight above to use cups and spoons.'
                    : ing.base_unit === 'ml'
                      ? ' Set the cup weight above to use grams and pounds.'
                      : ' Set what 1 each weighs above to use grams and pounds.'
                  : ''
              }`}
              error={displayError}
            />
          </FormGrid>
        </div>

        <div className="ing-block">
          <h4>Allergens</h4>
          <p className="ing-intro">
            Tick every one it contains. Guests see these on the menu for every dish that uses it.
          </p>
          <fieldset className="ing-allergens">
            <legend className="ing-sr">Allergens in {ing.name}</legend>
            {ALLERGENS.map((a) => (
              <label key={a} className="ing-allergen">
                <input
                  type="checkbox"
                  checked={allergens.includes(a)}
                  onChange={(e) =>
                    setAllergens((prev) =>
                      e.target.checked ? [...prev, a] : prev.filter((x) => x !== a),
                    )
                  }
                />
                {ALLERGEN_LABELS[a]}
              </label>
            ))}
          </fieldset>
          <AllergenList allergens={allergens} style={{ margin: 0 }} />
          <label className="ing-check-row" style={{ fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
              aria-describedby={reviewHintId}
            />
            I checked this ingredient's allergens
          </label>
          <p id={reviewHintId} className="ing-intro">
            {reviewHint}
          </p>
        </div>

        <div className="ing-actions">
          <span className="ing-actions-note" aria-live="polite">
            {dirty ? 'You have changes that are not saved yet.' : ''}
          </span>
          {dirty && (
            <Button type="button" variant="secondary" onClick={reset} disabled={save.isPending}>
              Undo changes
            </Button>
          )}
          <Button type="submit" disabled={!dirty || invalid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ---------- packages ---------- */

function packageSize(p: IngredientPackage, ing: Ingredient): string {
  const q = Number(p.quantity);
  const entered = `${formatNumber(q, 3)} ${getUnit(p.unit)?.abbr ?? p.unit}`;
  let base: number | null = null;
  try {
    base = toBaseQuantity(q, p.unit, ing);
  } catch {
    return `Holds ${entered}. Set the conversion above to use it.`;
  }
  if (p.unit === ing.base_unit) return `Holds ${entered}`;
  return `Holds ${entered}, about ${formatQty(base, { ...ing, display_unit: null }, 'entered')}`;
}

function PackagesCard({ ing, setFlash }: { ing: Ingredient; setFlash: (f: Flash) => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState(() => defaultUnitValue(ing));
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<IngredientPackage | null>(null);
  const qty = parseAmount(amount);
  const unitFields = splitUnitValue(unit);
  const amountError =
    qty !== null && !(isNumber(qty) && qty > 0) ? 'Enter an amount above zero.' : undefined;

  const add = useMutation({
    mutationFn: () =>
      api.post(`/admin/recipes/ingredients/${ing.id}/packages`, {
        label: label.trim(),
        quantity: qty,
        unit: 'unit' in unitFields ? unitFields.unit : ing.base_unit,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      setFlash({ kind: 'ok', text: `Added the ${label.trim()} package.` });
      setLabel('');
      setAmount('');
      setError(null);
    },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <Card>
      <div className="ing-card">
        <h3>Packages</h3>
        <p className="ing-intro">
          How it comes off the shelf, like a 28 oz can or a 50 lb bag. You can then count stock and
          buy it by the package.
        </p>
        {ing.packages.length > 0 ? (
          <ul className="ing-pkgs">
            {ing.packages.map((p) => (
              <li key={p.id} className="ing-pkg">
                <div>
                  <strong>{p.label}</strong>
                  <div className="ing-small ing-soft">{packageSize(p, ing)}</div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setRemoving(p)}
                  aria-label={`Remove the ${p.label} package`}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ing-soft ing-small">No packages yet.</p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!label.trim()) return setError('Give the package a name, like 28 oz can.');
            if (!(isNumber(qty) && qty > 0)) return setError('Enter how much one package holds.');
            setError(null);
            add.mutate();
          }}
        >
          <FormGrid min={160}>
            <Field
              label="Package name"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="28 oz can"
              maxLength={60}
            />
            <Field
              label="One package holds"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="28"
              error={amountError}
            />
            <UnitSelect
              label="Unit"
              ingredient={ing}
              value={unit}
              onValueChange={(v) => setUnit(v)}
              includePackages={false}
              showUnavailableHint={false}
            />
            <FormAction>
              <Button type="submit" variant="secondary" disabled={add.isPending}>
                {add.isPending ? 'Adding…' : 'Add package'}
              </Button>
            </FormAction>
          </FormGrid>
        </form>
        {error && <Notice kind="error">{error}</Notice>}
      </div>
      <ConfirmDelete
        open={removing !== null}
        onClose={() => setRemoving(null)}
        permission="recipes.manage"
        title={`Remove the ${removing?.label ?? ''} package?`}
        description="Recipe lines that used it keep their amounts. You can add it again any time."
        actionLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={async () => {
          if (!removing) return;
          await api.delete(`/admin/recipes/ingredients/${ing.id}/packages/${removing.id}`);
          await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
          setFlash({ kind: 'ok', text: `Removed the ${removing.label} package.` });
        }}
      />
    </Card>
  );
}

/* ---------- nutrition ---------- */

type Mode = 'label' | 'per100';
type NutrientText = Record<NutrientKey, string>;

const emptyNutrients = (): NutrientText =>
  Object.fromEntries(NUTRIENT_KEYS.map((k) => [k, ''])) as NutrientText;

function nutrientLabel(k: NutrientKey): string {
  const unit = NUTRIENT_UNITS[k];
  return unit === 'kcal' ? NUTRIENT_LABELS[k] : `${NUTRIENT_LABELS[k]} (${unit})`;
}

function NutritionCard({ ing, setFlash }: { ing: Ingredient; setFlash: (f: Flash) => void }) {
  const qc = useQueryClient();
  const stored = ing.nutrition_per_100g;
  const hasStored = hasNutrition(ing);
  const storedText = (): NutrientText => {
    const t = emptyNutrients();
    for (const k of NUTRIENT_KEYS) {
      const v = stored?.[k];
      if (typeof v === 'number') t[k] = String(Number(v.toFixed(4)));
    }
    return t;
  };
  const [mode, setMode] = useState<Mode>(hasStored ? 'per100' : 'label');
  const [per100, setPer100] = useState<NutrientText>(storedText);
  const [perServing, setPerServing] = useState<NutrientText>(emptyNutrients);
  const [servingText, setServingText] = useState('');
  const [servingUnit, setServingUnit] = useState(() =>
    unitValue(ing.base_unit === 'each' ? 'each' : ing.base_unit),
  );
  const [labelGramsText, setLabelGramsText] = useState('');
  const [source, setSource] = useState(ing.nutrition_source ?? '');
  const [sourceTouched, setSourceTouched] = useState(false);

  // Serving size, for entering numbers from a package label.
  const servingAmount = parseAmount(servingText);
  const servingOk = isNumber(servingAmount) && servingAmount > 0;
  const labelGrams = parseAmount(labelGramsText);
  const labelGramsOk = isNumber(labelGrams) && labelGrams > 0;
  const unitFields = splitUnitValue(servingUnit);
  let convertedGrams: number | null = null;
  let servingProblem: string | undefined;
  if (servingOk) {
    try {
      const u =
        'package_id' in unitFields
          ? { packageId: unitFields.package_id }
          : 'unit' in unitFields
            ? unitFields.unit
            : ing.base_unit;
      convertedGrams = fromBaseQuantity(toBaseQuantity(servingAmount, u, ing), 'g', ing);
    } catch (e) {
      servingProblem = `${e instanceof Error ? e.message : 'This unit does not convert to grams'}. Do that under Conversions, or type the grams printed on the label.`;
    }
  }
  const servingGrams = labelGramsOk ? labelGrams : convertedGrams;
  const unitAbbr =
    'unit' in unitFields
      ? (getUnit(unitFields.unit)?.abbr ?? unitFields.unit)
      : 'package_id' in unitFields
        ? (ing.packages.find((p) => p.id === unitFields.package_id)?.label ?? 'package')
        : '';
  const servingSize =
    mode === 'per100'
      ? '100 g'
      : servingOk
        ? `${servingText.trim()} ${unitAbbr}${servingGrams && unitAbbr !== 'g' ? ` (${formatNumber(servingGrams, servingGrams < 10 ? 1 : 0)}g)` : ''}`
        : 'Not set';

  // Parse the boxes of the current mode.
  const texts = mode === 'label' ? perServing : per100;
  const entered: Nutrients = {};
  const errors: Partial<Record<NutrientKey, string>> = {};
  for (const k of NUTRIENT_KEYS) {
    const n = parseAmount(texts[k]);
    if (n === null) continue;
    if (!isNumber(n) || n < 0) errors[k] = 'Enter a number, like 12 or 0.5.';
    else entered[k] = n;
  }
  const enteredCount = Object.keys(entered).length;
  const toSave: Nutrients = {};
  if (mode === 'per100') Object.assign(toSave, entered);
  else if (servingGrams)
    for (const k of NUTRIENT_KEYS) {
      const v = entered[k];
      if (v !== undefined) toSave[k] = Number(((v * 100) / servingGrams).toFixed(4));
    }

  // A barcode scan's source reads as words here; numbers changed by hand no longer claim it.
  const storedSource = sourceWords(ing.nutrition_source);
  const autoSource =
    mode === 'label'
      ? `Package label, serving size ${servingSize}`
      : (isReference(ing.nutrition_source) || BARCODE_SOURCE.test(ing.nutrition_source ?? '')) &&
          !samePer100(entered, stored)
        ? 'Entered by owner, per 100 g'
        : storedSource;
  const shownSource = sourceTouched ? source : autoSource;

  const dirty =
    mode === 'label'
      ? enteredCount > 0 || servingText.trim() !== ''
      : !samePer100(entered, stored) || (sourceTouched && source !== storedSource);
  const hasErrors = Object.keys(errors).length > 0;
  const blocker =
    mode === 'label'
      ? !servingOk
        ? 'Enter the serving size from the label.'
        : !servingGrams
          ? 'The serving size needs grams.'
          : enteredCount === 0
            ? 'Type the numbers from the label.'
            : null
      : null;
  const missingRequired = REQUIRED_NUTRIENT_KEYS.filter((k) => entered[k] === undefined);

  const save = useMutation({
    mutationFn: () => {
      const cleared = Object.keys(toSave).length === 0;
      return api.patch(`/admin/recipes/ingredients/${ing.id}`, {
        nutrition_per_100g: cleared ? null : toSave,
        // Emptying every box removes the nutrition, so no source is kept for it either.
        nutrition_source: cleared ? null : shownSource.trim() || null,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      await qc.invalidateQueries({ queryKey: ['recipes'] });
      setFlash({
        kind: 'ok',
        text:
          mode === 'label'
            ? `Saved the nutrition for ${ing.name}. The label numbers were turned into amounts per 100 g.`
            : `Saved the nutrition for ${ing.name}.`,
      });
    },
    onError: (e) => setFlash({ kind: 'error', text: errorText(e) }),
  });

  const setBox = (k: NutrientKey, v: string) =>
    (mode === 'label' ? setPerServing : setPer100)((prev) => ({ ...prev, [k]: v }));

  return (
    <Card>
      {/* The barcode block has its own form, so it sits beside the nutrition form, not inside it. */}
      <div className="ing-card" style={{ marginBottom: 20 }}>
        <h3>Nutrition</h3>
        {hasStored && isReference(ing.nutrition_source) && (
          <Notice>
            These are USDA reference values for a typical product, not the one you buy. Scan its
            barcode or copy the label on the product you buy.
          </Notice>
        )}
        {!hasStored && (
          <p className="ing-intro">
            No nutrition yet. The easiest way is to scan the product's barcode, or copy the
            Nutrition Facts from the package.
          </p>
        )}
        {hasStored && BARCODE_SOURCE.test(ing.nutrition_source ?? '') && (
          <p className="ing-intro">{sourceWords(ing.nutrition_source)}.</p>
        )}
        <BarcodeNutrition ing={ing} setFlash={setFlash} />
      </div>
      <form
        className="ing-card ing-nutri"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !hasErrors && !blocker) {
            setFlash(null);
            save.mutate();
          }
        }}
      >
        <h4>Or type the numbers</h4>
        <Chips
          legend="How to enter nutrition"
          value={mode}
          onChange={setMode}
          options={[
            ['label', 'From the package label'],
            ['per100', 'Per 100 g'],
          ]}
        />
        <div className="ing-nutri-grid">
          <div className="ing-nutri-fields">
            {mode === 'label' ? (
              <>
                <p className="ing-intro">
                  Type the serving size and the numbers exactly as printed. Crave works out the
                  amounts per 100 g when you save.
                </p>
                <FormGrid min={160}>
                  <Field
                    label="Serving size"
                    value={servingText}
                    onChange={(e) => setServingText(e.target.value)}
                    placeholder="1/4"
                    error={
                      servingAmount !== null && !servingOk
                        ? 'Enter an amount, like 1/4 or 2.'
                        : undefined
                    }
                    hint={
                      servingOk && convertedGrams !== null && !labelGramsOk
                        ? `About ${formatNumber(convertedGrams, convertedGrams < 10 ? 1 : 0)} g`
                        : undefined
                    }
                  />
                  <UnitSelect
                    label="Unit"
                    ingredient={ing}
                    value={servingUnit}
                    onValueChange={(v) => setServingUnit(v)}
                    showUnavailableHint={false}
                    error={labelGramsOk ? undefined : servingProblem}
                  />
                  <Field
                    label="Grams on the label"
                    inputMode="decimal"
                    value={labelGramsText}
                    onChange={(e) => setLabelGramsText(e.target.value)}
                    placeholder="Optional"
                    hint="Only if it is printed, like (30g)."
                    error={
                      labelGrams !== null && !labelGramsOk ? 'Enter grams above zero.' : undefined
                    }
                  />
                </FormGrid>
                <h4>Per serving</h4>
              </>
            ) : (
              <>
                <p className="ing-intro">
                  The amounts in 100 grams of {ing.name}. Leave a box empty when you don't know it.
                </p>
                <h4>Per 100 g</h4>
              </>
            )}
            <FormGrid min={140}>
              {NUTRIENT_KEYS.map((k) => (
                <Field
                  key={k}
                  label={nutrientLabel(k)}
                  inputMode="decimal"
                  value={texts[k]}
                  onChange={(e) => setBox(k, e.target.value)}
                  error={errors[k]}
                  placeholder={OPTIONAL_NUTRIENTS.has(k) ? 'Optional' : undefined}
                />
              ))}
            </FormGrid>
            <p className="ing-intro">
              Leave a box empty if the label doesn't list it. Only type 0 when the label says 0.
              {missingRequired.length > 0 && enteredCount > 0
                ? ` Still needed for a complete menu label: ${missingRequired.map((k) => NUTRIENT_LABELS[k]).join(', ')}.`
                : ''}
            </p>
            <TextAreaField
              label="Where these numbers came from"
              rows={shownSource.length > 80 ? 4 : 2}
              value={shownSource}
              onChange={(e) => {
                setSourceTouched(true);
                setSource(e.target.value);
              }}
              maxLength={500}
              placeholder="Package label, brand name"
              hint="Kept with the numbers so you know what to update later."
            />
          </div>
          <div className="ing-preview">
            <h4>Label preview</h4>
            {enteredCount > 0 ? (
              <NutritionFactsLabel
                perServing={entered}
                servingSize={servingSize}
                headingLevel={4}
                hideFootnote
              />
            ) : (
              <p className="ing-preview-empty">The label appears here as you type.</p>
            )}
            {mode === 'label' && servingGrams && Object.keys(toSave).length > 0 && (
              <details className="ing-saved">
                <summary>What gets saved, per 100 g</summary>
                <dl>
                  {NUTRIENT_KEYS.filter((k) => toSave[k] !== undefined).map((k) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <dt>{NUTRIENT_LABELS[k]}</dt>
                      <dd>
                        {formatNumber(toSave[k] ?? 0, 2)}
                        {NUTRIENT_UNITS[k] === 'kcal' ? '' : ` ${NUTRIENT_UNITS[k]}`}
                      </dd>
                    </div>
                  ))}
                </dl>
              </details>
            )}
          </div>
        </div>
        <div className="ing-actions">
          <span className="ing-actions-note" aria-live="polite">
            {dirty ? (blocker ?? 'You have changes that are not saved yet.') : ''}
          </span>
          <Button type="submit" disabled={!dirty || hasErrors || !!blocker || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save nutrition'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function samePer100(a: Nutrients, b: Nutrients | null): boolean {
  return NUTRIENT_KEYS.every((k) => {
    const x = a[k];
    const y = b?.[k];
    if (x === undefined || y === undefined) return x === y;
    return Math.abs(x - y) < 1e-6;
  });
}

/* ---------- barcode ---------- */

/** GET /admin/barcode/lookup/:upc */
interface BarcodeLookup {
  upc: string;
  source: 'usda_branded' | 'open_food_facts';
  source_id: string;
  source_name: string;
  nutrition_source: string;
  brand: string | null;
  product_name: string | null;
  serving_text: string | null;
  serving_grams: number | null;
  per: '100g' | '100ml';
  nutrients_per_100g: Nutrients;
  suggested_allergens: string[];
  allergen_basis:
    'ingredient_list' | 'allergen_tags' | 'allergen_tags_and_ingredient_list' | 'none';
  /** Plain sentence about the suggestions from the API; never says an allergen is absent. */
  allergen_note: string;
  notices: string[];
  usda_key: 'saved' | 'missing' | 'rejected' | 'unreadable';
  already_saved_on: {
    ingredient_id: string;
    ingredient_name: string;
    ingredient_active: boolean;
    package_id: string | null;
    package_label: string | null;
  }[];
}

/** nutrition_source written by a barcode scan: "usda_branded:<fdcId>" or "open_food_facts:<code>". */
const BARCODE_SOURCE = /^(usda_branded|open_food_facts):\S+$/;

/** A barcode-scan source in words; any other source as it was typed. */
function sourceWords(source: string | null | undefined): string {
  if (!source) return '';
  const m = BARCODE_SOURCE.exec(source);
  if (!m) return source;
  return m[1] === 'usda_branded'
    ? 'Barcode scan, product label from USDA FoodData Central'
    : 'Barcode scan, product from Open Food Facts';
}

function sameAmount(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return Math.abs(a - b) < 1e-6;
}

function amountWords(k: NutrientKey, v: number | undefined): string {
  if (v === undefined) return 'Not listed';
  const unit = NUTRIENT_UNITS[k];
  return `${formatNumber(v, v < 1 ? 2 : 1)}${unit === 'kcal' ? '' : ` ${unit}`}`;
}

/** "Scan barcode" and "Type the barcode number", then the review of what was found. */
function BarcodeNutrition({ ing, setFlash }: { ing: Ingredient; setFlash: (f: Flash) => void }) {
  const [scanning, setScanning] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | undefined>();
  const lookup = useMutation({
    mutationFn: async (digits: string) =>
      (await api.get<BarcodeLookup>(`/admin/barcode/lookup/${encodeURIComponent(digits)}`)).data,
  });
  const find = (digits: string) => {
    setFlash(null);
    lookup.reset();
    lookup.mutate(digits);
  };
  const typedDigits = typed.replace(/[\s-]/g, '');

  return (
    <div className="ing-barcode">
      <h4>Scan the barcode</h4>
      <p className="ing-intro">
        Fill in the Nutrition Facts from the product's barcode. You see everything before anything
        is saved.
      </p>
      {ing.upc && (
        <SavedBarcode
          key={ing.upc}
          ing={ing}
          upc={ing.upc}
          setFlash={setFlash}
          onRemoved={() => lookup.reset()}
        />
      )}
      <div className="ing-actions" style={{ justifyContent: 'flex-start' }}>
        <Button
          type="button"
          onClick={() => {
            setTyping(false);
            setScanning(true);
          }}
          disabled={lookup.isPending}
        >
          Scan barcode
        </Button>
        <Button
          type="button"
          variant="secondary"
          aria-expanded={typing}
          onClick={() => setTyping((v) => !v)}
          disabled={lookup.isPending}
        >
          Type the barcode number
        </Button>
      </div>
      {typing && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^\d{8,14}$/.test(typedDigits)) {
              setTypedError('Type the numbers printed under the bars, usually 12 or 13 of them.');
              return;
            }
            setTypedError(undefined);
            find(typedDigits);
          }}
        >
          <FormGrid min={200}>
            <Field
              label="Barcode number"
              inputMode="numeric"
              autoComplete="off"
              enterKeyHint="search"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              error={typedError}
              hint="The numbers printed under the bars."
            />
            <FormAction>
              <Button type="submit" variant="secondary" disabled={lookup.isPending}>
                Look it up
              </Button>
            </FormAction>
          </FormGrid>
        </form>
      )}
      <div aria-live="polite">
        {lookup.isPending && (
          <p className="ing-soft">
            Looking up barcode {lookup.variables}. This takes a few seconds.
          </p>
        )}
      </div>
      {lookup.isError && (
        <Notice kind="error">
          {errorText(lookup.error, 'The lookup did not work. Check the connection and try again.')}
        </Notice>
      )}
      {lookup.data && (
        <BarcodeReview
          key={`${lookup.data.upc}:${lookup.data.nutrition_source}`}
          ing={ing}
          found={lookup.data}
          onCancel={() => lookup.reset()}
          setFlash={setFlash}
        />
      )}
      <BarcodeScanner
        open={scanning}
        onClose={() => setScanning(false)}
        onDetected={(digits) => {
          setScanning(false);
          setTyped(digits);
          find(digits);
        }}
        onTypeInstead={() => {
          setScanning(false);
          setTyping(true);
        }}
      />
    </div>
  );
}

/**
 * The barcode saved on this ingredient, with a way to take it off (a scan saved on the wrong
 * ingredient, or a product no longer bought) so it can be saved on another ingredient.
 */
function SavedBarcode({
  ing,
  upc,
  setFlash,
  onRemoved,
}: {
  ing: Ingredient;
  upc: string;
  setFlash: (f: Flash) => void;
  onRemoved: () => void;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/barcode/ingredients/${ing.id}`),
    onSuccess: async () => {
      onRemoved();
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      setFlash({
        kind: 'ok',
        text: `Removed the barcode from ${ing.name}. Its nutrition and allergens were not changed.`,
      });
    },
  });

  return (
    <div className="ing-saved-upc">
      <p className="ing-small ing-soft">
        Barcode saved for {ing.name}: {upc}
      </p>
      {confirming ? (
        <>
          <p>
            Take barcode {upc} off {ing.name} and its packages? Do this when a scan was saved on the
            wrong ingredient, or you stopped buying this product. The nutrition stays.
          </p>
          {remove.isError && <Notice kind="error">{errorText(remove.error)}</Notice>}
          <div className="ing-actions" style={{ justifyContent: 'flex-start' }}>
            <Button
              type="button"
              variant="danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Yes, remove the barcode'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setConfirming(false);
                remove.reset();
              }}
              disabled={remove.isPending}
            >
              Keep it
            </Button>
          </div>
        </>
      ) : (
        <div className="ing-actions" style={{ justifyContent: 'flex-start' }}>
          <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
            Remove barcode
          </Button>
        </div>
      )}
    </div>
  );
}

/** What a barcode lookup found, next to what is saved now. Only Save writes anything. */
function BarcodeReview({
  ing,
  found,
  onCancel,
  setFlash,
}: {
  ing: Ingredient;
  found: BarcodeLookup;
  onCancel: () => void;
  setFlash: (f: Flash) => void;
}) {
  const qc = useQueryClient();
  const titleId = useId();
  const [packageId, setPackageId] = useState('');
  const stored: Nutrients = ing.nutrition_per_100g ?? {};
  const next = found.nutrients_per_100g;
  const keys = NUTRIENT_KEYS.filter((k) => stored[k] !== undefined || next[k] !== undefined);
  const nothing = Object.keys(next).length === 0;
  const missingRequired = missingNutrientKeys(next);
  // Other ingredients that hold this barcode (on the ingredient or a package), once each.
  const holders = [
    ...new Map(
      found.already_saved_on
        .filter((s) => s.ingredient_id !== ing.id)
        .map((s) => [s.ingredient_id, s] as const),
    ).values(),
  ];
  const holderNames = holders.map((h) => h.ingredient_name).join(' and ');
  const productLabel =
    [found.brand, found.product_name].filter(Boolean).join(' ') || `barcode ${found.upc}`;

  const save = useMutation({
    mutationFn: (moveBarcode: boolean) =>
      api.post<{ ok: true; package_label: string | null }>(`/admin/barcode/ingredients/${ing.id}`, {
        upc: found.upc,
        source: found.source,
        source_id: found.source_id,
        nutrition_per_100g: next,
        package_id: packageId || null,
        product_label: productLabel.slice(0, 200),
        move_barcode: moveBarcode,
      }),
    onSuccess: async (_res, moveBarcode) => {
      await qc.invalidateQueries({ queryKey: INGREDIENTS_KEY });
      await qc.invalidateQueries({ queryKey: ['recipes'] });
      setFlash({
        kind: 'ok',
        text: `Saved the nutrition for ${ing.name} from ${productLabel}.${
          moveBarcode ? ` The barcode was moved here from ${holderNames}.` : ''
        } Allergens were not changed, so check them under Allergens.`,
      });
      onCancel();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  return (
    <section className="ing-review" aria-labelledby={titleId}>
      <h4 id={titleId}>Check this is the right product</h4>
      <dl className="ing-review-facts">
        <dt>Product</dt>
        <dd>{found.product_name ?? 'No name listed'}</dd>
        <dt>Brand</dt>
        <dd>{found.brand ?? 'No brand listed'}</dd>
        <dt>Serving size</dt>
        <dd>{found.serving_text ?? 'Not listed'}</dd>
        <dt>Barcode</dt>
        <dd>{found.upc}</dd>
        <dt>Found in</dt>
        <dd>{found.source_name}</dd>
      </dl>
      {found.notices.map((n) => (
        <Notice key={n}>{n}</Notice>
      ))}
      {holders.length > 0 && (
        <Notice>
          Barcode {found.upc} is saved on {holderNames}
          {holders.every((h) => !h.ingredient_active)
            ? `, which ${holders.length > 1 ? 'are' : 'is'} no longer in use`
            : ''}
          . One product can belong to one ingredient. If this product is really {ing.name}, move the
          barcode here. {holderNames} {holders.length > 1 ? 'keep their' : 'keeps its'} nutrition
          and allergens.
        </Notice>
      )}
      {!nothing && (
        <div className="ing-compare-wrap">
          <table className="ing-compare">
            <caption className="ing-sr">
              Nutrition saved now and from the barcode, per 100 {found.per === '100ml' ? 'ml' : 'g'}
            </caption>
            <thead>
              <tr>
                <th scope="col">Per 100 {found.per === '100ml' ? 'ml' : 'g'}</th>
                <th scope="col">Saved now</th>
                <th scope="col">From the barcode</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k} data-changed={!sameAmount(stored[k], next[k]) || undefined}>
                  <th scope="row">{NUTRIENT_LABELS[k]}</th>
                  <td>{amountWords(k, stored[k])}</td>
                  <td>{amountWords(k, next[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!nothing && (
        <p className="ing-intro">
          Saving replaces all of {ing.name}'s nutrition with the numbers from the barcode.
          {missingRequired.length > 0
            ? ` Not listed for this product: ${missingRequired.map((k) => NUTRIENT_LABELS[k]).join(', ')}. Add those from the label afterwards for a complete menu label.`
            : ''}
        </p>
      )}
      <div className="ing-suggest" role="note">
        <strong>Suggested allergens (not saved)</strong>
        <p>
          {found.allergen_note} Saving does not change allergens. Tick its allergens and "I checked
          this ingredient's allergens" under Details yourself.
        </p>
      </div>
      {ing.packages.length > 0 && !nothing && (
        <FormGrid min={220}>
          <SelectField
            label="Also remember this barcode on a package"
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            options={[
              ['', 'No package'] as const,
              ...ing.packages.map((p) => [p.id, p.label] as const),
            ]}
            hint="So a later scan of that package is recognized."
          />
        </FormGrid>
      )}
      {save.isError && <Notice kind="error">{errorText(save.error)}</Notice>}
      <div className="ing-actions">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={save.isPending}>
          Not this product
        </Button>
        <Button
          type="button"
          onClick={() => save.mutate(holders.length > 0)}
          disabled={nothing || save.isPending}
        >
          {save.isPending
            ? 'Saving…'
            : holders.length > 0
              ? 'Move the barcode here and save'
              : 'Save this nutrition'}
        </Button>
      </div>
    </section>
  );
}
